import {
  hasAdditionalProperties,
  hasProperties,
  hasRequired,
  isAnyOf,
  isArrayWithItems,
  isOneOf,
  isTupleArray,
  JsonSchema,
  JsonSchemaDefinition,
  JsonSchemaNode,
} from "src/jsonSchema/types";

export const getProperties = (
  property: JsonSchemaNode,
): Record<string, JsonSchemaNode> | null =>
  hasProperties(property) ? property.properties : null;

export const getSchemaNodesFromUnion = (
  property: JsonSchemaNode,
): JsonSchemaNode[] => {
  if (isAnyOf(property)) return property.anyOf;
  if (isOneOf(property)) return property.oneOf;
  return [];
};

export const resolveRef = (
  ref: string,
  schema: JsonSchema,
): JsonSchemaNode | null => {
  if (ref.startsWith("#/$defs/")) {
    const defName = ref.replace("#/$defs/", "");
    return schema.$defs?.[defName] || null;
  }
  if (ref.startsWith("#/definitions/")) {
    const defName = ref.replace("#/definitions/", "");
    return schema.definitions?.[defName] || null;
  }
  return null;
};

export const getRequiredFields = (
  property: JsonSchemaNode,
  schema: JsonSchema,
): string[] => {
  if (hasRequired(property)) {
    return property.required;
  }
  if (property.$ref) {
    const resolved = resolveRef(property.$ref, schema);
    return resolved ? getRequiredFields(resolved, schema) : [];
  }
  if (isArrayWithItems(property) && !isTupleArray(property)) {
    const itemRequired = getRequiredFields(
      property.items as JsonSchemaNode,
      schema,
    );
    if (itemRequired) {
      return itemRequired;
    }
  }
  return [];
};

export const getChildProperties = (
  property: JsonSchemaNode,
  schema: JsonSchema,
): Record<string, JsonSchemaNode> | null => {
  // Direct properties
  if (hasProperties(property)) {
    return getProperties(property);
  }

  // Resolve $ref
  if (property.$ref) {
    const resolved = resolveRef(property.$ref, schema);
    if (resolved != null && hasProperties(resolved)) {
      return getProperties(resolved);
    }
  }

  // Handle additionalProperties with complex schema (including refs)
  if (hasAdditionalProperties(property)) {
    const additionalProperties = property.additionalProperties;
    // If additionalProperties has a $ref, resolve it and return its properties
    if (additionalProperties.$ref) {
      const resolved = resolveRef(additionalProperties.$ref, schema);
      if (resolved != null && hasProperties(resolved)) {
        return getProperties(resolved);
      }
    }
    // If additionalProperties itself has properties, return them
    if (
      typeof additionalProperties === "object" &&
      hasProperties(additionalProperties)
    ) {
      return getProperties(additionalProperties);
    }
  }

  // Handle arrays with complex items - they should be expandable to show item structure
  if (isArrayWithItems(property) && !isTupleArray(property)) {
    const itemProperties = getChildProperties(
      property.items as JsonSchemaNode,
      schema,
    );
    if (itemProperties) {
      return itemProperties;
    }
  }
  return null;
};

export const getDefinitions = (
  schema?: JsonSchema,
): Record<string, JsonSchemaDefinition> | undefined => {
  return schema?.$defs ?? schema?.definitions;
};

/**
 * Extract clean entity name from $ref ("e.g. "EntityName" from "#/$defs/EntityName")
 */
export const extractEntityNameFromRef = (ref: string): string => {
  const match = ref.match(/#\/(?:\$defs|definitions)\/(.+)$/);
  return match ? match[1] : ref;
};
